import {Component, EventEmitter, Output, OnInit} from '@angular/core';
import {
    FormArray,
    FormBuilder,
    FormGroup,
    Validators,
    AbstractControl,
    ValidationErrors
} from '@angular/forms';
import {Router} from '@angular/router';
import {Location} from '@angular/common';
import {FarmService} from '@services/farm.service';
import {LoaderService} from '@services/loader.service';
import {ToastrService} from 'ngx-toastr';
import {Observable} from 'rxjs';

@Component({
    selector: 'app-farm',
    templateUrl: './farm.component.html',
    styleUrl: './farm.component.scss'
})
export class FarmComponent implements OnInit {
    parentForm: FormGroup;
    @Output() close: EventEmitter<void> = new EventEmitter<void>();
    @Output() dataSaved: EventEmitter<void> = new EventEmitter<void>();
    openFarmIndex: number = 0; // Track the index of the currently open farm
    isLoading$: Observable<boolean>;
    errorMessage: string = '';
    public isAuthLoading = false;
    userDetail: any;

    private nameValidator(control: AbstractControl): ValidationErrors | null {
        if (!control.value) {
            return null;
        }
        const value = control.value.toString().trim();
        if (!/[a-zA-Z]/.test(value)) {
            return {invalidName: true};
        }
        return null;
    }

    private positiveNumberValidator(
        control: AbstractControl
    ): ValidationErrors | null {
        if (!control.value && control.value !== 0) {
            return null;
        }
        const value = parseFloat(control.value);
        if (isNaN(value) || value <= 0) {
            return {invalidNumber: true};
        }
        return null;
    }

    private houseNameValidator(
        control: AbstractControl
    ): ValidationErrors | null {
        if (!control.value) {
            return null;
        }
        const name = control.value.toString().trim().toLowerCase();
        if (name === '0' || name === 'house 0') {
            return {reservedName: true};
        }
        return null;
    }

    constructor(
        private fb: FormBuilder,
        private farmService: FarmService,
        private toastr: ToastrService,
        private loaderService: LoaderService,
        private router: Router,
        private location: Location
    ) {
        this.isLoading$ = this.loaderService.isLoading$;
    }

    ngOnInit(): void {
        this.userDetail = JSON.parse(localStorage.getItem('UserDetail'));
        this.parentForm = this.fb.group({
            farms: this.fb.array([this.createFarm(1)]) // Initialize with one farm with specific naming
        });
    }

    get farms(): FormArray {
        return this.parentForm.get('farms') as FormArray;
    }

    createFarm(farmIndex: number): FormGroup {
        return this.fb.group({
            id: [0],
            name: ['', [Validators.required, this.nameValidator.bind(this)]],
            type: [0, [Validators.required, Validators.min(1)]],
            // BREED - COMMENTED OUT
            // breedId: [0, [Validators.required, Validators.min(1)]],
            size: [
                0,
                [Validators.required, this.positiveNumberValidator.bind(this)]
            ],
            length: [
                null,
                [Validators.required, this.positiveNumberValidator.bind(this)]
            ],
            width: [
                null,
                [Validators.required, this.positiveNumberValidator.bind(this)]
            ],            
            waterTank: [
                null,
                [Validators.required, this.positiveNumberValidator.bind(this)]
            ],                        
            dieselTank: [
                null,
                [Validators.required, this.positiveNumberValidator.bind(this)]
            ],
            waterTankHeight: [
                null, 
                [Validators.required, Validators.min(0.01)]
            ],
            dieselTankHeight: [
                null,
                [Validators.required, Validators.min(0.01)]
            ],
            address: ['', [Validators.required, this.nameValidator.bind(this)]],
            userId: [this.userDetail.id],
            farmHouses: this.fb.array([
                this.createHouse0(),
                this.createFarmHouse(farmIndex, 1)
            ])
        });
    }

    createHouse0(): FormGroup {
        return this.fb.group({
            id: [0],
            farmId: [0],
            name: ['House 0'],
            length: [0],
            width: [0],
            feedingSiloCapacity:[0],
            feedingSiloHeight:[0],
            farmHousePartitions: this.fb.array([this.createPartition0()])
        });
    }

    createPartition0(): FormGroup {
        return this.fb.group({
            id: [0],
            farmId: [0],
            farmHouseId: [0],
            name: ['Partition 0'],
            length: [0],
            capacity: [0],
            sensors: [[]]
        });
    }

    createFarmHouse(farmIndex: number, farmHouseIndex: number): FormGroup {
        return this.fb.group({
            id: [0],
            farmId: [0],
            name: [
                '',
                [
                    Validators.required,
                    this.nameValidator.bind(this),
                    this.houseNameValidator.bind(this)
                ]
            ],
            length: [
                null,
                [Validators.required, this.positiveNumberValidator.bind(this)]
            ],
            width: [
                null,
                [Validators.required, this.positiveNumberValidator.bind(this)]
            ],
            feedingSiloCapacity: [
                null,
                [Validators.required, this.positiveNumberValidator.bind(this)]
            ],
             feedingSiloHeight: [
                null,
                [Validators.required, this.positiveNumberValidator.bind(this)]
            ],
            farmHousePartitions: this.fb.array([
                this.createFarmHousePartition(farmIndex, farmHouseIndex, 1)
            ]) // Initialize with one partition with specific naming
        });
    }

    createFarmHousePartition(
        farmIndex: number,
        farmHouseIndex: number,
        partitionIndex: number
    ): FormGroup {
        return this.fb.group({
            id: [0],
            farmId: [0],
            farmHouseId: [0],
            name: ['', [Validators.required, this.nameValidator.bind(this)]],
            length: [
                null,
                [Validators.required, this.positiveNumberValidator.bind(this)]
            ],
            capacity: [
                null,
                [Validators.required, this.positiveNumberValidator.bind(this)]
            ],
            sensors: [[]]
        });
    }

    addFarm(): void {
        const farmIndex = this.farms.length + 1;
        this.farms.push(this.createFarm(farmIndex));
    }

    addFarmHouse(farmIndex: number): void {
        const farmHouseArray = this.farms
            .at(farmIndex)
            .get('farmHouses') as FormArray;
        const farmHouseIndex = farmHouseArray.length + 1;
        farmHouseArray.push(
            this.createFarmHouse(farmIndex + 1, farmHouseIndex)
        );
    }

    addFarmHousePartition(farmIndex: number, farmHouseIndex: number): void {
        const farmHouseArray = this.farms
            .at(farmIndex)
            .get('farmHouses') as FormArray;
        const partitionArray = farmHouseArray
            .at(farmHouseIndex)
            .get('farmHousePartitions') as FormArray;
        const partitionIndex = partitionArray.length + 1;
        partitionArray.push(
            this.createFarmHousePartition(
                farmIndex + 1,
                farmHouseIndex + 1,
                partitionIndex
            )
        );
    }

    removeFarm(index: number): void {
        if (this.farms.length > 1) {
            this.farms.removeAt(index);
            if (this.openFarmIndex === index) {
                this.openFarmIndex = this.farms.length > 0 ? 0 : null;
            } else if (this.openFarmIndex > index) {
                this.openFarmIndex--;
            }
        }
    }

    setOpenFarm(index: number): void {
        this.openFarmIndex = this.openFarmIndex === index ? null : index;
    }
    isFormValid(): boolean {
        return (
            this.parentForm.valid && this.areNestedFormsValid(this.parentForm)
        );
    }

    // Recursively check if nested forms are valid
    private areNestedFormsValid(formGroup: FormGroup): boolean {
        let isValid = true;

        Object.values(formGroup.controls).forEach((control) => {
            if (control instanceof FormGroup) {
                if (!this.areNestedFormsValid(control)) {
                    isValid = false;
                }
            } else if (control instanceof FormArray) {
                (control as FormArray).controls.forEach((subGroup) => {
                    if (subGroup instanceof FormGroup) {
                        if (!this.areNestedFormsValid(subGroup)) {
                            isValid = false;
                        }
                    }
                });
            }
        });

        return isValid;
    }

    submitForm(): void {
        //this.markFormGroupTouched(this.parentForm);

        this.loaderService.showLoader();
        this.isAuthLoading = true;
        if (this.parentForm.valid) {
            const formData = this.parentForm.value.farms.map((farm) => {
                farm.farmHouses = farm.farmHouses.map((farmHouse) => {
                    farmHouse.farmHousePartitions =
                        farmHouse.farmHousePartitions.map((partition) => {
                            // Ensure sensor IDs are correctly extracted
                            partition.sensors = partition.sensors.map(
                                (sensor) => ({
                                    id: sensor.id || sensor // Handle both object and raw ID cases
                                })
                            );
                            return partition;
                        });
                    return farmHouse;
                });
                return farm;
            });
            //const formData = this.parentForm.value.farms;
            this.farmService.addFarm(formData).subscribe({
                next: (response) => {
                    setTimeout(() => {
                        this.isAuthLoading = false;
                        this.loaderService.hideLoader();
                        this.toastr.success('Farm Added Successfully !');
                        this.router.navigate(['/farms-information']);
                        this.close.emit();
                    }, 2000);
                },
                error: (error) => {
                    this.toastr.error(error.error);
                    this.isAuthLoading = false;
                    this.loaderService.hideLoader();
                }
            });
        } else {
            this.isAuthLoading = false;
            this.loaderService.hideLoader();
            this.toastr.error('Please fill the required fields');
        }
    }

    goBack(): void {
        this.location.back();
    }
}
